import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Link di reset non valido: manca il token.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="reset-back-link"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#5C5B5A] hover:text-[#1C1B1A] mb-8"
        >
          <ArrowLeft size={14} /> Torna al login
        </Link>

        <div className="border border-[#E5E4E0] bg-white p-8 md:p-10">
          <div className="mb-6">
            <div className={`w-12 h-12 border rounded-full flex items-center justify-center mb-4 ${done ? "border-[#2C4C3B]" : "border-[#2C4C3B]"}`}>
              {done ? <CheckCircle2 size={20} className="text-[#2C4C3B]" /> : <KeyRound size={20} className="text-[#2C4C3B]" />}
            </div>
            <div className="label-mono">Reset password</div>
            <h1 className="font-display text-3xl mt-2 font-light">
              {done ? "Password aggiornata" : "Crea la nuova password"}
            </h1>
          </div>

          {!token ? (
            <div className="text-sm space-y-3">
              <div className="flex items-start gap-2 text-[#991B1B]">
                <AlertTriangle size={16} className="mt-0.5" />
                <p>Token mancante o non valido. Richiedi un nuovo link di reset.</p>
              </div>
              <Link to="/forgot-password">
                <Button className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase tracking-[0.2em] text-xs py-6 mt-4">
                  Richiedi nuovo link
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div className="space-y-4 text-sm text-[#1C1B1A]" data-testid="reset-success-message">
              <p>La password è stata aggiornata con successo.</p>
              <p className="text-xs text-[#5C5B5A]">Vieni reindirizzato al login tra pochi secondi…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="label-mono">Nuova password</Label>
                <Input
                  required
                  type="password"
                  minLength={6}
                  autoFocus
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                />
              </div>
              <div>
                <Label className="label-mono">Conferma password</Label>
                <Input
                  required
                  type="password"
                  minLength={6}
                  data-testid="reset-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ripeti la password"
                  className="mt-1 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-button"
                className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase tracking-[0.2em] text-xs py-6"
              >
                {loading ? "Aggiornamento…" : "Aggiorna password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
